import type z from 'zod';
import type { ZodTypeAny } from 'zod';
import type { ValidateWithZodOptions, ZodValidationSource } from './zod-api-utils/zod-error-types';
import { ZodError } from 'zod';
import { isStaging } from '@/libs/getEnvironment';
import { formatZodIssues, getValidationContext, getValidationDetails, getValidationOptions, logUnexpectedValidationError } from './zod-api-utils/zod-api-error-handlers';

export function validateWithZod<TSchema extends ZodTypeAny, K>(
  schema: TSchema,
  target: K,
  options: ZodValidationSource | ValidateWithZodOptions = 'backend',
): z.infer<TSchema> {
  if (!schema) {
    throw new Error('Schema is not defined! Please define a schema for the response.');
  }

  const validationOptions = getValidationOptions(options);

  try {
    return schema.parse(target);
  } catch (err) {
    if (err instanceof ZodError) {
      const issues = formatZodIssues(err);
      const details = getValidationDetails(issues);
      const validationContext = getValidationContext(schema, validationOptions, issues.length);
      const title = `[Zod validation failed] ${validationContext.schema}`;

      if (isStaging()) {
        console.error(title, validationContext, details);
      } else {
        console.warn(title, validationContext, details);
      }
    } else {
      // Non-Zod exceptions indicate implementation bugs: throwing refinements,
      // accidental async schema usage with `.parse`, or other unexpected crashes.
      // Log them separately from contract drift. Backend validation must remain
      // non-blocking so diagnostics cannot take down production UI.
      logUnexpectedValidationError(schema, validationOptions, err);
    }

    if (validationOptions.from === 'frontend') {
      throw err;
    }

    // Backend contract drift should be visible in logs, but non-critical API
    // response mismatches should not reject the query and take down the UI.
    return target as z.infer<TSchema>;
  }
}
